import {
    Box,
    Checkbox,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    Typography,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { useAppContext } from "../../../shared/contexts/app-context/components/AppContext";
import {
    useAutoOrderConfiguration,
    useOrders,
    usePortfolioManagement,
    usePortfolioProfile,
    usePortfolioSelection,
} from "../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import {
    useActionConfirmationDialog
} from "../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import { OrderUpdateModel } from "../../portfolio-info/types/OrderUpdateModel";
import { OrderModel } from "../../portfolio-info/types/OrderModel";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { orderMappers } from "../../portfolio-info/mappers/OrderMappers";
import { dateTimeConversionService } from "../../../shared/services/DateTimeConversionService";
import { AssetType } from "../../../shared/types/AssetType";
import { assetService } from "../../../shared/services/AssetService";
import { mathCalculationService } from "../../../shared/services/MathCalculationService";
import { blackScholesModelCalculationService } from "../../../shared/services/BlackScholesModelCalculationService";
import { ContextMenu, OrdersToolsPanel } from "../controls";
import { ApplicationTooltip, LineDelimiter, OptionExpirationTableCell } from "../views";
import { ContextMenuItem } from "../controls/context-menu/ContextMenuItem";
import { PortfolioProfile } from "../../../shared/types/PortfolioProfile";
import { ChangingParameters } from "../types";
import {
    ActionConfirmationDialog
} from "../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog";
import { OrderInfoTooltipTitle } from "../../portfolio-info/components/OrderInfoTooltipTitle";
import { TableBodyRow, TableHeaderRow } from "../../shared/views";
import {
    ORDERS_TABLE_ROW_PADDING_Y,
    TABLE_PARAMETER_COLUMN_MIN_WIDTH,
    TABLE_SYMBOL_EXP_CELL_FONT_SIZE
} from "../../shared/constants/table.constants";
import { numberFormatters } from "../../shared/utils/numberFormatters";
import { OrderState } from "../../../shared/types/OrderState";
import type { MarketDataDto } from "../../../shared/types/MarketDataDto";

import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AutofpsSelectIcon from '@mui/icons-material/AutofpsSelect';
import { OrderManagerDialog } from "../dialogs/order-manager-dialog/OrderManagerDialog";
import { AutoOrderManagerDialog } from "../dialogs/auto-order-manager-dialog/AutoOrderManagerDialog";
import { AutoOrderConfigurationModel } from "../../portfolio-info/types/AutoOrderConfigurationModel";
import { TargetPlatform } from "../../../shared/types/TargetPlatform";
import { orderArchiveUtils } from "../utils/OrderArchiveUtils";
import { TargetPlatformContextMenuItem } from "../controls/context-menu/TargetPlatformContextMenuItem";
import { OrderFilters, orderFilterUtils } from "../utils/OrderFilterUtils";
import { getTargetPlatformText } from "../utils/TargetPlatformUtils";
import { ordersService } from "../../../shared/services/orders-service/OrdersService";

type OrdersProps = {
    isExpanded: boolean;
    setIsExpanded: Dispatch<SetStateAction<boolean>>;
};

export function Orders({
    isExpanded,
    setIsExpanded,
}: OrdersProps) {
    const {
        marketDataDto,
    } = useAppContext();
    
    const {
        selectedPortfolio,
        isAllPortfoliosSelected,
    } = usePortfolioSelection();
    const {
        fetchSelectedPortfolio,
        saveSelectedPortfolio,
    } = usePortfolioManagement();
    const {
        portfolioProfile,
        updateSelectedPortfolio,
    } = usePortfolioProfile();
    const {
        addFutureToPortfolio,
        updateOrderInSelectedPortfolio,
        deleteOrderFromSelectedPortfolio,
    } = useOrders();
    const {
        saveAutoOrderConfiguration,
        fetchAutoOrderConfiguration,
        resetAutoOrderConfigurationState,
    } = useAutoOrderConfiguration();

    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        confirmationMessage,
        submitButtonName,
    } = useActionConfirmationDialog();

    const [submitEditedOrder, setSubmitEditedOrder] = useState<(portfolioModel: OrderUpdateModel) => Promise<void>>();
    const [editingOrderModel, setEditingOrderModel] = useState<OrderUpdateModel | null>(null);
    const [isEditOrderDialogOpen, setIsEditOrderDialogOpen] = useState<boolean>(false);
    const [isNewOrderModel, setIsNewOrderModel] = useState<boolean>(false);
    const [autoOrderOrderModel, setAutoOrderOrderModel] = useState<OrderModel | null>(null);
    const [isAutoOrderManagerDialogOpen, setIsAutoOrderManagerDialogOpen] = useState<boolean>(false);
    const [selectedOrderStateFilter, setSelectedOrderStateFilter] = useState<OrderState | null>(null);
    const [selectedAssetTypeFilter, setSelectedAssetTypeFilter] = useState<AssetType | null>(null);
    const [selectedTargetPlatformFilter, setSelectedTargetPlatformFilter] = useState<TargetPlatform | null>(null);
    const [filtersSourcePortfolio, setFiltersSourcePortfolio] = useState(selectedPortfolio);
    const [selectedOrder, setSelectedOrder] = useState<OrderModel | null>(null);
    
    const orders = selectedPortfolio !== null
        ? selectedPortfolio.orders
        : [];

    const selectedFilters: OrderFilters = {
        orderState: selectedOrderStateFilter,
        assetType: selectedAssetTypeFilter,
        targetPlatform: selectedTargetPlatformFilter,
    };
    const filterAvailability = orderFilterUtils.getAvailability(orders, selectedFilters);
    const orderFilterSignature = Array.from(new Set(orders.map(order =>
        `${order.state}:${order.type}:${order.targetPlatform}`)))
        .sort()
        .join('|');

    const filteredOrders = orders.filter(order =>
        (selectedOrderStateFilter === null || order.state === selectedOrderStateFilter) &&
        (selectedAssetTypeFilter === null || order.type === selectedAssetTypeFilter) &&
        (selectedTargetPlatformFilter === null || order.targetPlatform === selectedTargetPlatformFilter));
    
    const initialRowsPerPage = 15;
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const orderCount = filteredOrders.length;
    const [page, setPage] = useState(0);

    const [isPortfolioChanged, setIsPortfolioChanged] = useState(false);

    useEffect(() => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            setSelectedOrderStateFilter(null);
            setSelectedAssetTypeFilter(null);
            setSelectedTargetPlatformFilter(null);
            setFiltersSourcePortfolio(selectedPortfolio);
            return;
        }

        setSelectedOrderStateFilter(selectedPortfolio.portfolioSettings.orderStateFilter);
        setSelectedAssetTypeFilter(selectedPortfolio.portfolioSettings.ordersAssetTypeFilter);
        setSelectedTargetPlatformFilter(selectedPortfolio.portfolioSettings.ordersTargetPlatformFilter);
        setFiltersSourcePortfolio(selectedPortfolio);
    }, [selectedPortfolio]);

    useEffect(() => {
        if (
            selectedPortfolio === null ||
            selectedPortfolio.portfolioSettings === null ||
            filtersSourcePortfolio !== selectedPortfolio
        ) {
            return;
        }

        const normalizedFilters = orderFilterUtils.normalize(selectedFilters, filterAvailability);
        if (orderFilterUtils.areEqual(selectedFilters, normalizedFilters)) {
            return;
        }

        setSelectedOrderStateFilter(normalizedFilters.orderState);
        setSelectedAssetTypeFilter(normalizedFilters.assetType);
        setSelectedTargetPlatformFilter(normalizedFilters.targetPlatform);

        selectedPortfolio.portfolioSettings.orderStateFilter = normalizedFilters.orderState;
        selectedPortfolio.portfolioSettings.ordersAssetTypeFilter = normalizedFilters.assetType;
        selectedPortfolio.portfolioSettings.ordersTargetPlatformFilter = normalizedFilters.targetPlatform;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    }, [
        selectedPortfolio,
        filtersSourcePortfolio,
        selectedOrderStateFilter,
        selectedAssetTypeFilter,
        selectedTargetPlatformFilter,
        orderFilterSignature,
    ]);

    useEffect(() => {
        if (
            !isEditOrderDialogOpen ||
            isNewOrderModel ||
            editingOrderModel === null
        ) {
            return;
        }

        setSubmitEditedOrder(() => async (orderUpdateModel: OrderUpdateModel): Promise<void> => {
            const currentOrderModel = selectedPortfolio?.orders.find(order =>
                orderUpdateModel.externalId > 0
                    ? order.externalId === orderUpdateModel.externalId
                    : order.internalId === orderUpdateModel.internalId);

            if (currentOrderModel === undefined) {
                return;
            }

            orderMappers.map(orderUpdateModel, currentOrderModel);
            await updateOrderInSelectedPortfolioWrapper(true, true);
        });
    }, [
        selectedPortfolio,
        editingOrderModel?.externalId,
        editingOrderModel?.internalId,
        isEditOrderDialogOpen,
        isNewOrderModel,
    ]);

    const handleOrderStateFilterChange = (value: SetStateAction<OrderState | null>): void => {
        const nextOrderStateFilter = typeof value === 'function'
            ? value(selectedOrderStateFilter)
            : value;

        setSelectedOrderStateFilter(nextOrderStateFilter);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.orderStateFilter = nextOrderStateFilter;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleAssetTypeFilterChange = (value: SetStateAction<AssetType | null>): void => {
        const nextAssetTypeFilter = typeof value === 'function'
            ? value(selectedAssetTypeFilter)
            : value;

        setSelectedAssetTypeFilter(nextAssetTypeFilter);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.ordersAssetTypeFilter = nextAssetTypeFilter;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleTargetPlatformFilterChange = (value: SetStateAction<TargetPlatform | null>): void => {
        const nextTargetPlatformFilter = typeof value === 'function'
            ? value(selectedTargetPlatformFilter)
            : value;

        setSelectedTargetPlatformFilter(nextTargetPlatformFilter);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.ordersTargetPlatformFilter = nextTargetPlatformFilter;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleResetFilters = (): void => {
        setSelectedOrderStateFilter(null);
        setSelectedAssetTypeFilter(null);
        setSelectedTargetPlatformFilter(null);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.orderStateFilter = null;
        selectedPortfolio.portfolioSettings.ordersAssetTypeFilter = null;
        selectedPortfolio.portfolioSettings.ordersTargetPlatformFilter = null;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };
    
    useEffect(() => {
        setPage(getLastPageIndex(rowsPerPage, orderCount));
        setIsPortfolioChanged(selectedPortfolio?.orders.some(x => x.isChanged) ?? false);
    }, [selectedPortfolio, rowsPerPage, orderCount]);
    
    const updateOrderInSelectedPortfolioWrapper = async (
        refreshOrderParameters: boolean, saveToStorage: boolean
    ): Promise<void> => {
        await updateOrderInSelectedPortfolio(refreshOrderParameters, saveToStorage);
        setIsPortfolioChanged(orders.some(x => x.isChanged));
    }
    
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };
    
    const getLastPageIndex = (rowsPerPage: number, orderCount: number): number => {
        const fullPageCount = rowsPerPage !== -1
            ? Math.floor(orderCount / rowsPerPage)
            : 1;
        
        return rowsPerPage !== -1
            ? fullPageCount * rowsPerPage === orderCount
                ? fullPageCount - 1
                : fullPageCount
            : 0;
    }
    
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rowsPerPage = +event.target.value;
        setPage(getLastPageIndex(rowsPerPage, orderCount));
        setRowsPerPage(rowsPerPage);
    };

    const handleOrderArchiveStatusChanging = async (order: OrderModel): Promise<void> => {
        if (isAllPortfoliosSelected || orderArchiveUtils.isArchiveControlDisabled(order.state, order.targetPlatform)) {
            return;
        }

        order.state = order.state === OrderState.Archived
            ? OrderState.Active
            : OrderState.Archived;

        await saveSelectedPortfolio();
        
        // await updateOrderInSelectedPortfolioWrapper(true, true);
    };

    const handleOrderActiveStatusChanging = async (event: React.ChangeEvent<HTMLInputElement>, order: OrderModel): Promise<void> => {
        if (isAllPortfoliosSelected || order.state === OrderState.Archived) {
            return;
        }

        order.state = event.target.checked
            ? OrderState.Active
            : OrderState.Disabled;

        await updateOrderInSelectedPortfolioWrapper(true, false);
    };
    
    const handleEditOrder = (orderModel: OrderModel, closeContextMenu?: () => void | null) => {
        if (closeContextMenu)
            closeContextMenu();

        if (isAllPortfoliosSelected) {
            return;
        }
        
        setEditingOrderModel(orderMappers.mapForEditing(orderModel));
        setIsNewOrderModel(false);
        setSubmitEditedOrder(() => async (orderUpdateModel: OrderUpdateModel): Promise<void> => {
            const currentOrderModel = selectedPortfolio?.orders.find(order =>
                order.externalId === orderUpdateModel.externalId ||
                order.internalId === orderUpdateModel.internalId);

            if (currentOrderModel === undefined) {
                return;
            }

            orderMappers.map(orderUpdateModel, currentOrderModel);
            await updateOrderInSelectedPortfolioWrapper(true, true);
        });
        setIsEditOrderDialogOpen(true);
    };
    
    const deleteEditingOrder = () => async () =>{
        if (isAllPortfoliosSelected) {
            return;
        }

        setSelectedOrder(null);
        await deleteOrderFromSelectedPortfolio(editingOrderModel!.internalId);
    }
    
    const getOpenPrice = (): number => {
        return selectedPortfolio?.portfolioSettings?.useCustomPrice
            ? selectedPortfolio.portfolioSettings.customPrice
            : marketDataDto?.BaseAsset.Price ?? 0
    }
    
    const openPrice = getOpenPrice();
    
    const handleAddOrder = () => {
        if (isAllPortfoliosSelected) {
            return;
        }

        const underlyingAsset = marketDataDto?.BaseAsset;
        const size = 1;
        
        const newOrderModel: OrderModel = new OrderModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Active,
            targetPlatform: TargetPlatform.None,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: null,
            impliedVolatilityAtOpen: null,
            customImpliedVolatility: null,
            symbol: `${underlyingAsset!.BaseAsset}/USDT`,
            size: size,
            openPrice: openPrice,
            markPrice: openPrice,
            markImpliedVolatility: null,
            type: AssetType.Futures,
            strike: null,
            underlyingPriceAtOpen: null,
            delta: Math.sign(size),
            gamma: null,
            vega: null,
            theta: null,
            comment: null,
            expirationPrice: null,
            dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
            impliedVolatilityForPricing: null,
            underlyingPriceForPricing: openPrice,
        });
        
        setEditingOrderModel(orderMappers.mapForEditing(newOrderModel));
        setIsNewOrderModel(true);
        setSubmitEditedOrder(() => async (orderUpdateModel: OrderUpdateModel): Promise<void> => {
            orderMappers.map(orderUpdateModel, newOrderModel);
            await addFutureToPortfolio(newOrderModel);
        });
        setIsEditOrderDialogOpen(true);
    };
    
    const handleOnOrderClick = (order: OrderModel) => {
        setSelectedOrder(order);
    };
    
    const handleUpdateCreationDateTime = async (order: OrderModel, closeContextMenu: () => void) => {
        closeContextMenu();
        if (isAllPortfoliosSelected) {
            return;
        }
        order.openedPositionAt = dateTimeConversionService.getCurrentUtcDate();
        await updateOrderInSelectedPortfolio(false, true);
    }

    const handleTargetPlatformChange = async (
        order: OrderModel,
        targetPlatform: TargetPlatform,
    ): Promise<void> => {
        if (isAllPortfoliosSelected || order.state === OrderState.Archived) {
            return;
        }

        order.targetPlatform = targetPlatform;
        ordersService.recalculateFee(order);
        order.setChangedState();
        await updateOrderInSelectedPortfolioWrapper(false, true);
    };
    
    const handleAddClosingOrder = async (order: OrderModel, closeContextMenu: () => void) => {
        closeContextMenu();
        await cloneOrder(order, true);
    };

    const handleCloneOrder = async (order: OrderModel, closeContextMenu: () => void) => {
        closeContextMenu();
        await cloneOrder(order, false);
    };

    const cloneOrder = async (order: OrderModel, shouldInvertOrderSize: boolean) => {
        if (
            isAllPortfoliosSelected ||
            marketDataDto === undefined ||
            selectedPortfolio === null || selectedPortfolio.portfolioSettings === null
        ) {
            return;
        }

        const portfolioSettings = selectedPortfolio!.portfolioSettings!;
        const underlyingPrice = portfolioSettings.useCustomPrice
            ? portfolioSettings.customPrice
            : marketDataDto.BaseAsset.Price ?? 0;

        const getOpenPrice = () => {
            if (assetService.isOption(order)) {
                const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                    .getTimeToExpirationInShareOfYear(order.expireAt!);

                return blackScholesModelCalculationService.getPremium(
                    order.type, underlyingPrice, order.strike!,
                    timeToExpirationInShareOfYear, order.markImpliedVolatility!);
            } else {
                return underlyingPrice;
            }
        }

        const openPrice = getOpenPrice();

        const newOrderModel: OrderModel = new OrderModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Active,
            targetPlatform: TargetPlatform.None,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: order.expireAt,
            impliedVolatilityAtOpen: order.markImpliedVolatility,
            customImpliedVolatility: order.markImpliedVolatility,
            symbol: order.symbol,
            size: shouldInvertOrderSize ? -order.size : order.size,
            openPrice: openPrice,
            markPrice: openPrice,
            markImpliedVolatility: order.markImpliedVolatility,
            type: order.type,
            strike: order.strike,
            underlyingPriceAtOpen: underlyingPrice,
            delta: 0,
            gamma: null,
            vega: null,
            theta: null,
            comment: null,
            expirationPrice: order.expirationPrice,
            dateForPricing: order.dateForPricing,
            impliedVolatilityForPricing: order.impliedVolatilityForPricing,
            underlyingPriceForPricing: order.underlyingPriceForPricing,
        });

        await addFutureToPortfolio(newOrderModel);
    };

    const handleRemoveOrder = async (order: OrderModel, closeContextMenu: () => void) => {
        closeContextMenu();
        
        if (isAllPortfoliosSelected || order.state !== OrderState.Disabled) {
            return;
        }

        await deleteOrderFromSelectedPortfolio(order.internalId);
    };

    const isAutoOrderAvailable = (order: OrderModel): boolean =>
        !isAllPortfoliosSelected && assetService.isOption(order) && order.externalId !== 0;

    const handleOpenAutoOrderManager = (order: OrderModel, closeContextMenu: () => void): void => {
        closeContextMenu();

        if (isAllPortfoliosSelected || !isAutoOrderAvailable(order)) {
            return;
        }

        setAutoOrderOrderModel(order);
        setIsAutoOrderManagerDialogOpen(true);
    };

    const handleAutoOrderConfigurationSaved = (configuration: AutoOrderConfigurationModel): void => {
        if (isAllPortfoliosSelected) {
            return;
        }

        const order = selectedPortfolio?.orders.find(
            order => order.externalId === configuration.orderId) ?? null;

        if (order !== null) {
            order.autoOrderConfiguration = configuration;
        }

        if (autoOrderOrderModel?.externalId === configuration.orderId) {
            setAutoOrderOrderModel(order ?? autoOrderOrderModel);
        }

        if (editingOrderModel?.externalId === configuration.orderId) {
            setEditingOrderModel({
                ...editingOrderModel,
                autoOrderConfiguration: configuration,
            });
        }

        void updateSelectedPortfolio();
    };

    const reloadEditingOrder = async (marketData: MarketDataDto | null): Promise<OrderUpdateModel | null> => {
        if (isAllPortfoliosSelected || editingOrderModel === null || editingOrderModel.externalId <= 0) {
            return null;
        }

        const reloadedPortfolio = await fetchSelectedPortfolio(marketData);
        const reloadedOrder = reloadedPortfolio?.orders.find(
            order => order.externalId === editingOrderModel.externalId) ?? null;

        if (reloadedOrder === null) {
            return null;
        }

        const reloadedEditingModel = orderMappers.mapForEditing(reloadedOrder);
        setEditingOrderModel(reloadedEditingModel);

        return reloadedEditingModel;
    };
    
    const getPortfolioCurrentDeltaAtPrice = (
        portfolioProfile: PortfolioProfile,
        price: number
    ): number | null => {
        const records = portfolioProfile.data;

        for (let i = 0; i < records.length - 1; i++) {
            if (records[i].price <= price && records[i + 1].price > price ||
                records[i].price >= price && records[i + 1].price < price)
            {
                return records[i].currentDelta;
            }
        }

        return null;
    }
    
    const getCurrentPortfolioDeltaNeutralizingOrderSize = () =>
        mathCalculationService.round(
            0 - (getPortfolioCurrentDeltaAtPrice(portfolioProfile, openPrice) ?? 0),
            3);

    const deltaNeutralizingOrderSize = getCurrentPortfolioDeltaNeutralizingOrderSize();
        
    const handleAddDeltaNeutralizingOrder = async (closeContextMenu: () => void) => {
        closeContextMenu();
        
        if (
            isAllPortfoliosSelected ||
            marketDataDto === undefined ||
            selectedPortfolio === null || selectedPortfolio.portfolioSettings === null ||
            portfolioProfile === null
        ) {
            return;
        }

        const portfolioSettings = selectedPortfolio!.portfolioSettings!;
        const underlyingPrice = portfolioSettings.useCustomPrice
            ? portfolioSettings.customPrice
            : marketDataDto.BaseAsset.Price ?? 0;

        const getOpenPrice = (): number => {
            return selectedPortfolio?.portfolioSettings?.useCustomPrice
                ? selectedPortfolio.portfolioSettings.customPrice
                : marketDataDto?.BaseAsset.Price ?? 0
        }
        
        const underlyingAsset = marketDataDto?.BaseAsset;
        const openPrice = getOpenPrice();

        const newOrderModel: OrderModel = new OrderModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Active,
            targetPlatform: TargetPlatform.None,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: null,
            impliedVolatilityAtOpen: null,
            customImpliedVolatility: null,
            symbol: `${underlyingAsset!.BaseAsset}/USDT`,
            size: deltaNeutralizingOrderSize,
            openPrice: openPrice,
            markPrice: openPrice,
            markImpliedVolatility: null,
            type: AssetType.Futures,
            strike: null,
            underlyingPriceAtOpen: underlyingPrice,
            delta: deltaNeutralizingOrderSize,
            gamma: null,
            vega: null,
            theta: null,
            comment: null,
            expirationPrice: null,
            dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
            impliedVolatilityForPricing: null,
            underlyingPriceForPricing: underlyingPrice,
        });

        await addFutureToPortfolio(newOrderModel);
    };
    
    const buildChangingParameters = (): ChangingParameters => {
        return {
            selectedOrder,
            marketDataDto,
            orders,
            updateOrderInSelectedPortfolio: updateOrderInSelectedPortfolioWrapper,
            recalculateOrderFee: order => {
                ordersService.recalculateFee(order);
            },
        };
    }

    return (
        <Stack
            sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                paddingTop: '8px',
            }}
        >
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                submitButtonName={submitButtonName}
                confirmedParentAction={confirmedAction}
            />
            <OrderManagerDialog
                isOpen={isEditOrderDialogOpen}
                setIsOpen={setIsEditOrderDialogOpen}
                submitAction={submitEditedOrder}
                deleteAction={deleteEditingOrder}
                editingModel={editingOrderModel}
                isNew={isNewOrderModel}
                icon={isNewOrderModel ? <AddBoxIcon /> : <EditIcon />}
                onAutoOrderConfigurationSaved={handleAutoOrderConfigurationSaved}
                saveAutoOrderConfiguration={saveAutoOrderConfiguration}
                fetchAutoOrderConfiguration={fetchAutoOrderConfiguration}
                resetAutoOrderConfigurationState={resetAutoOrderConfigurationState}
                reloadEditingOrder={reloadEditingOrder}
            />
            <AutoOrderManagerDialog
                isOpen={isAutoOrderManagerDialogOpen}
                setIsOpen={setIsAutoOrderManagerDialogOpen}
                orderId={autoOrderOrderModel?.externalId ?? 0}
                optionOrderSymbol={autoOrderOrderModel?.symbol ?? ""}
                optionOrderSize={autoOrderOrderModel?.size ?? 0}
                optionImpliedVolatility={autoOrderOrderModel?.markImpliedVolatility ?? null}
                underlyingPrice={marketDataDto?.UnderlyingPrice ?? marketDataDto?.BaseAsset.Price ?? null}
                autoOrderConfiguration={autoOrderOrderModel?.autoOrderConfiguration ?? null}
                onConfigurationSaved={handleAutoOrderConfigurationSaved}
                saveAutoOrderConfiguration={saveAutoOrderConfiguration}
                fetchAutoOrderConfiguration={fetchAutoOrderConfiguration}
                resetAutoOrderConfigurationState={resetAutoOrderConfigurationState}
                icon={<AutofpsSelectIcon />}
            />
            <OrdersToolsPanel
                selectedOrderStateFilter={selectedOrderStateFilter}
                setSelectedOrderStateFilter={handleOrderStateFilterChange}
                selectedAssetTypeFilter={selectedAssetTypeFilter}
                setSelectedAssetTypeFilter={handleAssetTypeFilterChange}
                selectedTargetPlatformFilter={selectedTargetPlatformFilter}
                setSelectedTargetPlatformFilter={handleTargetPlatformFilterChange}
                filterAvailability={filterAvailability}
                resetFilters={handleResetFilters}
                isPortfolioChanged={isPortfolioChanged}
                setIsPortfolioChanged={setIsPortfolioChanged}
                changingParameters={buildChangingParameters()}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
            />
            <LineDelimiter/>
            <Paper
                sx={{
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 0,
                }}
            >
                <TableContainer
                    sx={{
                        flex: '1',
                        padding: '0px',
                        margin: '0px',
                        overflowX: 'auto',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{padding: '0px', margin: '0px', verticalAlign: 'text-top'}}
                    >
                        <TableHead>
                            <TableHeaderRow>
                                <ApplicationTooltip
                                    title="Target platform: B = Binance, D = Deribit, S = Samsung Wallet, T = Trust Wallet, R = RevolutX"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>T</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="If checked, the order is archived"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>A</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="If checked, the order is included in the portfolio profile calculation"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>S</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Edit order"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>E</TableCell>
                                </ApplicationTooltip>
                                <TableCell sx={{"&&":{textAlign: 'left'}}}>Symbol</TableCell>
                                <TableCell>Exp</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Volume</TableCell>
                                <TableCell>Fee</TableCell>
                                <ApplicationTooltip
                                    title="Implied volatility at the time of execution"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>IV,%</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip title="Delta" disableInteractive offset={[0, -7]}>
                                    <TableCell sx={{minWidth: TABLE_PARAMETER_COLUMN_MIN_WIDTH}}>D</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Gamma is displayed in millionths (10⁻⁶)"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell sx={{minWidth: TABLE_PARAMETER_COLUMN_MIN_WIDTH}}>G</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip title="Vega" disableInteractive offset={[0, -7]}>
                                    <TableCell sx={{minWidth: TABLE_PARAMETER_COLUMN_MIN_WIDTH}}>V</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip title="Theta" disableInteractive offset={[0, -7]}>
                                    <TableCell sx={{minWidth: TABLE_PARAMETER_COLUMN_MIN_WIDTH}}>T</TableCell>
                                </ApplicationTooltip>
                                <TableCell sx={{"&&":{textAlign: 'left'}}}>Note</TableCell>
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {filteredOrders
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .sort((x: OrderModel, y: OrderModel) =>
                                    new Date(x.openedPositionAt).getTime() >
                                    new Date(y.openedPositionAt).getTime()
                                        ? 1
                                        : -1)
                                .map((order) => (
                                    <ContextMenu<OrderModel>
                                        renderItems={(order, closeContextMenu) => (
                                            <>
                                                <ContextMenuItem
                                                    onClick={() => handleEditOrder(order, closeContextMenu)}
                                                    disabled={isAllPortfoliosSelected}
                                                >
                                                    Edit
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => handleUpdateCreationDateTime(order, closeContextMenu)}
                                                    disabled={isAllPortfoliosSelected || order.state !== OrderState.Disabled}
                                                >
                                                    Update creation time
                                                </ContextMenuItem>
                                                <TargetPlatformContextMenuItem
                                                    value={order.targetPlatform}
                                                    onValueChange={(targetPlatform) =>
                                                        handleTargetPlatformChange(order, targetPlatform)}
                                                    closeContextMenu={closeContextMenu}
                                                    disabled={isAllPortfoliosSelected || order.state === OrderState.Archived}
                                                />
                                                <ContextMenuItem
                                                    onClick={() => handleAddClosingOrder(order, closeContextMenu)}
                                                    disabled={isAllPortfoliosSelected}
                                                >
                                                    Add closing order
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => handleCloneOrder(order, closeContextMenu)}
                                                    disabled={isAllPortfoliosSelected}
                                                >
                                                    Add cloned order
                                                </ContextMenuItem>
                                                 <ApplicationTooltip
                                                     placement='right-start'
                                                     title={
                                                        order.state !== OrderState.Disabled
                                                            ? "Only inactive orders can be removed"
                                                            : null
                                                     }
                                                 >
                                                     <span>
                                                         <ContextMenuItem
                                                             onClick={() => handleRemoveOrder(order, closeContextMenu)}
                                                             disabled={isAllPortfoliosSelected || order.state !== OrderState.Disabled}
                                                         >
                                                            Remove order
                                                         </ContextMenuItem>
                                                     </span>
                                                 </ApplicationTooltip>
                                                <ContextMenuItem
                                                    onClick={() => handleAddDeltaNeutralizingOrder(closeContextMenu)}
                                                    disabled={isAllPortfoliosSelected || deltaNeutralizingOrderSize === 0}
                                                >
                                                    {
                                                        deltaNeutralizingOrderSize !== 0
                                                            ? `Add delta-neutralizing order: ${deltaNeutralizingOrderSize}`
                                                            : 'Add delta-neutralizing order'
                                                    }
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => handleOpenAutoOrderManager(order, closeContextMenu)}
                                                    disabled={!isAutoOrderAvailable(order)}
                                                >
                                                    Auto Order Manager...
                                                </ContextMenuItem>
                                            </>
                                        )}
                                    >
                                        {({onContextMenu}) => (
                                            <TableBodyRow
                                                onClick={() => handleOnOrderClick(order)}
                                                onContextMenu={
                                                    (event: React.MouseEvent) => {
                                                        setSelectedOrder(order);
                                                        onContextMenu(event, order)
                                                    }
                                                }
                                                key={order.internalId}
                                                paddingY={ORDERS_TABLE_ROW_PADDING_Y}
                                                style={{
                                                    ...(selectedOrder && selectedOrder.externalId === order.externalId
                                                        ? {backgroundColor: 'rgba(255,224,122,0.5)'}
                                                        : {}),
                                                }}
                                            >
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        "&&": {
                                                            paddingX: '0px',
                                                            minWidth: '25px'
                                                        },
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {getTargetPlatformText(order.targetPlatform)}
                                                </TableCell>
                                                <ApplicationTooltip
                                                    title={orderArchiveUtils.getArchiveControlTooltip(
                                                        order.state,
                                                        order.targetPlatform)}
                                                    disableInteractive
                                                    offset={[0, -7]}
                                                >
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            "&&": {
                                                                paddingX: "2px",
                                                            },
                                                        }}
                                                    >
                                                        {
                                                            order.state === OrderState.Archived
                                                                ?
                                                                    <IconButton
                                                                        color="default"
                                                                        size="small"
                                                                        sx={{padding: '0px'}}
                                                                       onClick={() => handleOrderArchiveStatusChanging(order)}
                                                                        disabled={isAllPortfoliosSelected}
                                                                    >
                                                                        <LockIcon />
                                                                    </IconButton>
                                                                :
                                                                    <IconButton
                                                                        color="default"
                                                                        size="small"
                                                                        sx={{padding: '0px'}}
                                                                        onClick={() => handleOrderArchiveStatusChanging(order)}
                                                                        disabled={isAllPortfoliosSelected || orderArchiveUtils.isArchiveControlDisabled(
                                                                            order.state,
                                                                            order.targetPlatform)}
                                                                    >
                                                                        <LockOpenIcon />
                                                                    </IconButton>
                                                        }
                                                    </TableCell>
                                                </ApplicationTooltip>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        "&&": {
                                                            paddingX: "2px",
                                                        },
                                                    }}
                                                >
                                                    <ApplicationTooltip
                                                        title={
                                                            order.state === OrderState.Archived
                                                                ? "Archived orders can’t be set to inactive"
                                                                : null
                                                        }
                                                        disableInteractive
                                                    >
                                                        <span>
                                                            <Checkbox
                                                                color="default"
                                                                checked={order.state === OrderState.Active || order.state === OrderState.Archived}
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleOrderActiveStatusChanging(event, order)}
                                                                sx={{
                                                                    padding: '0px',
                                                                    '&.Mui-disabled': {
                                                                        color: 'rgba(0, 0, 0, 0.25)',
                                                                    },
                                                                }}
                                                                disabled={isAllPortfoliosSelected || order.state === OrderState.Archived}
                                                            />
                                                        </span>
                                                    </ApplicationTooltip>
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        "&&": {
                                                            paddingX: '2px',
                                                        },
                                                    }}
                                                >
                                                    <IconButton
                                                        color="warning"
                                                        size="small"
                                                        sx={{padding: '0px'}}
                                                        onClick={() => handleEditOrder(order)}
                                                        disabled={isAllPortfoliosSelected}
                                                    >
                                                        <EditIcon/>
                                                    </IconButton>
                                                </TableCell>
                                                <ApplicationTooltip
                                                    title={<OrderInfoTooltipTitle orderModel={order}/>}
                                                    maxWidth='450px'
                                                    offset={[0, -7]}
                                                    disableInteractive
                                                >
                                                    <TableCell align="left">
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontSize: TABLE_SYMBOL_EXP_CELL_FONT_SIZE,
                                                                textOverflow: 'ellipsis',
                                                                ...(order.isChanged
                                                                    ? {color: 'rgb(213,1,1)'}
                                                                    : {}),
                                                            }}
                                                        >
                                                            {orderMappers.getSymbolName(order.symbol, order.type)}
                                                        </Typography>
                                                    </TableCell>
                                                </ApplicationTooltip>
                                                <OptionExpirationTableCell
                                                    isOption={assetService.isOption(order)}
                                                    expireAt={order.expireAt}
                                                    expirationPrice={order.expirationPrice}
                                                    textColour={order.isChanged
                                                        ? 'rgb(213,1,1)'
                                                        : undefined}
                                                />
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {numberFormatters.format4(order.size)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {numberFormatters.format0(order.openPrice)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {numberFormatters.format0(-(order.size * order.openPrice))}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {order.fee.toFixed(2)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {order.impliedVolatilityAtOpen !== null
                                                        ? numberFormatters.format2(order.impliedVolatilityAtOpen * 100)
                                                        : ''
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {

                                                        assetService.isOption(order)
                                                            ? !assetService.isOptionExpired(order)
                                                                ? numberFormatters.format3(order.delta)
                                                                : ''
                                                            : numberFormatters.format3(order.delta)
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {
                                                        order.gamma !== null &&
                                                        assetService.isOption(order) &&
                                                        !assetService.isOptionExpired(order)
                                                            ? numberFormatters.format0(order.gamma * 10 ** 6)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {
                                                        order.vega !== null &&
                                                        assetService.isOption(order) &&
                                                        !assetService.isOptionExpired(order)
                                                            ? numberFormatters.format0(order.vega)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {
                                                        order.theta !== null &&
                                                        assetService.isOption(order) &&
                                                        !assetService.isOptionExpired(order)
                                                            ? numberFormatters.format0(order.theta)
                                                            : null
                                                    }
                                                </TableCell>
                                                <ApplicationTooltip
                                                    offset={[0, -7]}
                                                    title={order.comment}
                                                >
                                                    <TableCell
                                                        align="left"
                                                        sx={{
                                                            maxWidth: '100px',
                                                            ...(order.isChanged
                                                                ? {color: 'rgb(213,1,1)'}
                                                                : {}),
                                                        }}
                                                    >
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                flex: 1,
                                                                fontSize: '14px',
                                                                textOverflow: 'ellipsis',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            {order.comment}
                                                        </Typography>
                                                    </TableCell>
                                                </ApplicationTooltip>
                                            </TableBodyRow>
                                        )}
                                    </ContextMenu>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Stack direction="row" justifyContent="space-between">
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            fontSize: '14px',
                            marginBottom: '-6px'
                        }}
                    >
                        <IconButton
                            color="success"
                            sx={{
                                padding: '0px',
                                paddingLeft: '8px',
                                marginBottom: '-2px'
                            }}
                            onClick={handleAddOrder}
                            disabled={isAllPortfoliosSelected}
                        >
                            <AddBoxIcon/>
                        </IconButton>
                        <Box sx={{paddingLeft: '5px', fontWeight: 'bold'}}>
                            New order
                        </Box>
                    </Stack>
                    <TablePagination
                        showFirstButton
                        showLastButton
                        rowsPerPageOptions={[8, 15, 20, 25, 100]}
                        component="div"
                        count={orderCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            height: 'auto',
                            marginBottom: '-10px',
                            marginLeft: '-10px'
                        }}
                    />
                </Stack>
            </Paper>
        </Stack>
    );
}
